import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axiosInstance from "../../../api/axiosInstance";
import { handleQuickFill, handleSmartCardRead } from "../../../utils/functions";
import moment from "moment";

const COLORS = {
  gray:   '#5B6B79',
  blue:   '#4680ff',
  green:  '#2CA87F',
  red:    '#9E2B25',
  orange: '#F29E4C',
};

const lbl = {
  fontSize: 10, fontWeight: 700, color: COLORS.gray,
  textTransform: 'uppercase', letterSpacing: '0.05em',
  display: 'block', marginBottom: 3,
};

export const inp = {
  width: '100%', height: 32, borderRadius: 7, border: '1px solid #dde3eb',
  padding: '0 9px', fontSize: 12, color: '#2d3748', outline: 'none', background: '#fff',
};

const err = { fontSize: 10, color: COLORS.red, marginTop: 2 };

const SectionHead = ({ icon, label, color }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10,
    paddingBottom: 6, borderBottom: `2px solid ${color}22`,
  }}>
    <div style={{
      width: 24, height: 24, borderRadius: 6, background: `${color}18`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <i className={icon} style={{ color, fontSize: 13 }} />
    </div>
    <span style={{ fontSize: 11, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {label}
    </span>
  </div>
);

const Field = ({ label, required, error, children }) => (
  <div>
    <label style={lbl}>{label}{required && <span style={{ color: COLORS.red }}> *</span>}</label>
    {children}
    {error && <div style={err}>{error}</div>}
  </div>
);

const AddPatientForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [patients12, setPatients12] = useState([]);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    gender: "",
    nationality: "",
    lastVisitDate: "",
    civilNumber: "",
    passportNumber: "",
    mobileNumber: "",
    email: "",
    address: "",
    fileOpenedDate: moment().format("YYYY-MM-DD"),
    firstVisitDate: "",
    defaultDoctorId: "",
    emergencyContactName: "",
    emergencyContactRelation: "",
    emergencyContactPhone1: "",
    emergencyContactPhone2: "",
    legacyFile: "",
    base64Image: "",
  });

  const [files, setFiles] = useState({
    profileImage: null,
    cprScan: null,
    passportCopy: null,
  });

  // ✅ NEW: Pre-fill from pending online booking
  useEffect(() => {
    const pending = location.state?.onlineAppointmentData || localStorage.getItem("pendingOnlineBooking");
    if (pending) {
      try {
        const data = typeof pending === "string" ? JSON.parse(pending) : pending;
        setFormData(prev => ({
          ...prev,
          firstName: data.appointmentFirstName || "",
          lastName: data.appointmentLastName || "",
          email: data.appointmentEmail || "",
          mobileNumber: data.appointmentPhoneNumber || ""
        }));
      } catch (e) {
        console.error("Error parsing pending booking:", e);
      }
    }
  }, []);

  const [readingCard, setReadingCard] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [doctors, setDoctors] = useState([]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const { name, files: selectedFiles } = e.target;
    setFiles((prev) => ({ ...prev, [name]: selectedFiles[0] }));
  };

  const handleRemoveImage = (e) => {
    e.preventDefault();
    setFormData((prev) => ({ ...prev, base64Image: "" }));
  };

  useEffect(() => {
    getdata12();
  }, []);

  const getdata12 = async () => {
    try {
      const response = await axiosInstance.get(`getNationalitiesList`);
      if (response.data.success === true) {
        setPatients12(response.data.data);
      } else {
        console.error("Failed to fetch patient data:", response.data.message);
      }
    } catch (error) {
      console.error("Error fetching patient data:", error.message);
    }
  };

  const validate = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{8,15}$/;
    const today = new Date().toISOString().split("T")[0];
    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";

    if (!formData.dateOfBirth)
      newErrors.dateOfBirth = "Date of birth is required";
    else if (formData.dateOfBirth > today)
      newErrors.dateOfBirth = "Date of birth cannot be in future";
    if (!formData.gender) newErrors.gender = "Gender is required";
    if (!formData.nationality)
      newErrors.nationality = "Nationality is required";
    if (!formData.civilNumber.trim())
      newErrors.civilNumber = "Civil ID is required";
    if (formData.mobileNumber.trim() && !phoneRegex.test(formData.mobileNumber))
      newErrors.mobileNumber = "Invalid phone number";
    if (formData.email && !emailRegex.test(formData.email))
      newErrors.email = "Invalid email format";
    if (formData.firstVisitDate && formData.firstVisitDate > today)
      newErrors.firstVisitDate = "First visit date cannot be in the future";
    if (!formData.defaultDoctorId)
      newErrors.defaultDoctorId = "Doctor selection is required";
    // Profile image is NOT required
    if (files.profileImage) {
      const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];

      if (!allowedTypes.includes(files.profileImage.type)) {
        newErrors.profileImage = "Only JPG, JPEG, or PNG images are allowed";
      }

      const maxSize = 2 * 1024 * 1024; // 2 MB
      if (files.profileImage.size > maxSize) {
        newErrors.profileImage = "Image size must be less than 2MB";
      }
    }

    console.error(newErrors)
    return newErrors;
  };
  const [errors, setErrors] = useState({});
  const handleSubmit = async (e) => {
    e.preventDefault();
    const foundErrors = validate();
    if (Object.keys(foundErrors).length > 0) {
      setErrors(foundErrors);
      setSubmitted(false);
      return;
    }
    const data = new FormData();
    data.append("firstName", formData.firstName);
    data.append("lastName", formData.lastName);
    data.append("dateOfBirth", formData.dateOfBirth);
    data.append("base64Image", formData.base64Image);
    data.append("gender", formData.gender);
    data.append("nationality", formData.nationality);
    data.append("civilIdNumber", formData.civilNumber);
    data.append("passportNumber", formData.passportNumber);
    data.append("mobileNumber", formData.mobileNumber);
    data.append("email", formData.email);
    data.append("address", formData.address || "");
    data.append("fileOpenedDate", formData.fileOpenedDate);
    data.append("firstVisitDate", formData.firstVisitDate);
    data.append("defaultDoctorId", formData.defaultDoctorId);
    data.append("emContactName", formData.emergencyContactName);
    data.append("emContactRelation", formData.emergencyContactRelation);
    data.append("emContactPhone1", formData.emergencyContactPhone1);
    data.append("emContactPhone2", formData.emergencyContactPhone2);
    data.append("legacyf", formData.legacyFile);
    data.append("lastVisitDate", formData.lastVisitDate);

    if (files.profileImage) data.append("profileImage", files.profileImage);
    if (files.cprScan) data.append("cprScan", files.cprScan);
    if (files.passportCopy) data.append("passportCopy", files.passportCopy);
    try {
      const response = await axiosInstance.post(`registerPatient`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (response.data.success === true) {
        setSubmitted(true);
        Swal.fire(
          "Success!!",
          "Patient has been Added Successfully.",
          "success"
        );

        const pending = location.state?.onlineAppointmentData || localStorage.getItem("pendingOnlineBooking");
        if (pending) {
          // CORRECT EXTRACTION: check user's specified path first
          const newPatientId =
            response.data.login_details?.patientId ||
            response.data.login_details?.id ||
            response.data.patientId ||
            response.data.data?.id ||
            response.data.id;

          const patientName = `${formData.firstName} ${formData.lastName}`;
          const appointmentData = typeof pending === "string" ? JSON.parse(pending) : pending;

          // Set both state (Option A) and localStorage (Option B) for safety
          localStorage.setItem("returnFromAddPatient", "true");
          if (newPatientId) {
            localStorage.setItem("newlyRegisteredPatientId", newPatientId);
          }
          localStorage.setItem("newlyRegisteredPatientName", patientName);

          navigate("/Admin/appointment2", {
            state: {
              newPatientCreated: true, // Key updated
              patientId: newPatientId, // Key updated
              patientName: patientName,
              onlineAppointmentData: appointmentData
            }
          });
        } else {
          navigate("/Admin/patient");
        }
        // toast.success("Patient added successfully!");
      } else {
        Swal.fire("Warning", response.data.message, "Warning");
      }
    } catch (error) {
      console.error("Submit error:", error);
      Swal.fire("Warning",
        (error.response?.data?.message || error.message), "Warning");
    }
  };

  useEffect(() => {
    getDoctors();
  }, []);
  const getDoctors = async () => {
    try {
      const response = await axiosInstance.get(`getActiveDoctors`);
      if (response.data.success === true) {
        const docs = response.data.data;
        setDoctors(docs);

        // ✅ NEW: Try to match doctor if this is from online booking
        const pending = localStorage.getItem("pendingOnlineBooking");
        if (pending) {
          const data = JSON.parse(pending);
          const serviceName = data.appointmentServiceName?.toLowerCase();
          const matchedDoctor = docs.find(d =>
            d.displayName.toLowerCase().includes(serviceName) ||
            (d.specialization && d.specialization.toLowerCase().includes(serviceName))
          );
          if (matchedDoctor) {
            setFormData(prev => ({ ...prev, defaultDoctorId: matchedDoctor.id }));
          }
        }
      } else {
        console.error("epi error");
      }
    } catch (error) {
      console.error(error);
    }
  };
  return (
    <div className="pc-container">
      <div className="pc-content">
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0', overflow: 'hidden' }}>

          {/* ── Header ── */}
          <div style={{ padding: '12px 20px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={() => navigate(-1)}
              style={{
                width: 30, height: 30, borderRadius: 8, border: '1px solid #e8ecf0',
                background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, flexShrink: 0,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = COLORS.blue + '22'; e.currentTarget.style.color = COLORS.blue }}
              onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fb'; e.currentTarget.style.color = COLORS.gray }}
            >
              <i className="ti ti-arrow-left" />
            </button>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: `${COLORS.blue}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="ti ti-user-plus" style={{ color: COLORS.blue, fontSize: 17 }} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Register Patient</div>
              <div style={{ fontSize: 11, color: COLORS.gray }}>Fill in patient details to create a new record</div>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              <button
                title="Read from SmartCard Reader (USB)"
                onClick={() => handleSmartCardRead(setFormData, formData, setReadingCard)}
                disabled={readingCard}
                style={{
                  height: 30, padding: '0 12px', borderRadius: 7, border: '1px solid #A8892A',
                  background: '#f8f9fb', color: '#B5962A', cursor: readingCard ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600,
                }}
              >
                <i className={readingCard ? 'fas fa-spinner fa-spin' : 'fas fa-microchip'} style={{ fontSize: 14 }} />
                {readingCard ? 'Reading...' : 'Read Card'}
              </button>
              <button
                title="Quick Fill from CPR Extract XML File"
                onClick={() => handleQuickFill(setFormData, formData)}
                style={{
                  height: 30, padding: '0 12px', borderRadius: 7, border: '1px solid #dde3eb',
                  background: '#f8f9fb', color: COLORS.red, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600,
                }}
              >
                <i className="fas fa-id-card" style={{ fontSize: 14 }} /> XML
              </button>
            </div>
          </div>

          {/* ── Form ── */}
          <form onSubmit={handleSubmit} encType="multipart/form-data" style={{ padding: '16px 20px' }}>

            {/* Personal Info */}
            <div style={{ marginBottom: 16 }}>
              <SectionHead icon="ti ti-user" label="Personal Information" color={COLORS.blue} />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px 14px' }}>
                <Field label="First Name" required error={errors.firstName}>
                  <input style={inp} type="text" name="firstName" placeholder="First name" value={formData.firstName} onChange={handleChange} />
                </Field>
                <Field label="Last Name" required error={errors.lastName}>
                  <input style={inp} type="text" name="lastName" placeholder="Last name" value={formData.lastName} onChange={handleChange} />
                </Field>
                <Field label="Date of Birth" required error={errors.dateOfBirth}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={formData.dateOfBirth ? dayjs(formData.dateOfBirth) : null}
                      onChange={(v) => setFormData(p => ({ ...p, dateOfBirth: v ? dayjs(v).format('YYYY-MM-DD') : '' }))}
                      slotProps={{ textField: { size: 'small', sx: { '& .MuiInputBase-root': { height: 32, fontSize: 12, borderRadius: '7px' }, width: '100%' } } }}
                    />
                  </LocalizationProvider>
                </Field>
                <Field label="Gender" required error={errors.gender}>
                  <select style={inp} name="gender" value={formData.gender} onChange={handleChange}>
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </Field>
                <Field label="Nationality" required error={errors.nationality}>
                  <select style={inp} name="nationality" value={formData.nationality} onChange={handleChange}>
                    <option value="">Select</option>
                    {patients12.map(item => <option key={item.id} value={item.nationality}>{item.nationality}</option>)}
                  </select>
                </Field>
                <Field label="Civil ID Number" required error={errors.civilNumber}>
                  <input style={inp} type="text" name="civilNumber" placeholder="Civil ID" value={formData.civilNumber} onChange={handleChange} />
                </Field>
                <Field label="Passport Number" error={errors.passportNumber}>
                  <input style={inp} type="text" name="passportNumber" placeholder="Passport no." value={formData.passportNumber} onChange={handleChange} />
                </Field>
                <Field label="Doctor" required error={errors.defaultDoctorId}>
                  <select style={inp} name="defaultDoctorId" value={formData.defaultDoctorId} onChange={handleChange}>
                    <option value="">Select Doctor</option>
                    {doctors.map((d, i) => <option key={i} value={d.id}>{d.displayName}</option>)}
                  </select>
                </Field>
              </div>
            </div>

            {/* Contact Info */}
            <div style={{ marginBottom: 16 }}>
              <SectionHead icon="ti ti-phone" label="Contact Information" color={COLORS.green} />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px 14px' }}>
                <Field label="Mobile Number" error={errors.mobileNumber}>
                  <input style={inp} type="text" name="mobileNumber" placeholder="Mobile" value={formData.mobileNumber} onChange={handleChange} />
                </Field>
                <Field label="Email" error={errors.email}>
                  <input style={inp} type="email" name="email" placeholder="email@example.com" value={formData.email} onChange={handleChange} />
                </Field>
                <Field label="Address">
                  <input style={inp} type="text" name="address" placeholder="Address" value={formData.address} onChange={handleChange} />
                </Field>
                <Field label="File Opened Date">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={formData.fileOpenedDate ? dayjs(formData.fileOpenedDate) : null}
                      onChange={(v) => setFormData(p => ({ ...p, fileOpenedDate: v ? dayjs(v).format('YYYY-MM-DD') : '' }))}
                      slotProps={{ textField: { size: 'small', sx: { '& .MuiInputBase-root': { height: 32, fontSize: 12, borderRadius: '7px' }, width: '100%' } } }}
                    />
                  </LocalizationProvider>
                </Field>
                <Field label="Legacy File">
                  <input style={inp} type="text" name="legacyFile" placeholder="Legacy file reference" value={formData.legacyFile} onChange={handleChange} />
                </Field>
              </div>
            </div>

            {/* Emergency Contact */}
            <div style={{ marginBottom: 16 }}>
              <SectionHead icon="ti ti-alert-circle" label="Emergency Contact" color={COLORS.orange} />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px 14px' }}>
                <Field label="Contact Name">
                  <input style={inp} type="text" name="emergencyContactName" placeholder="Contact name" value={formData.emergencyContactName} onChange={handleChange} />
                </Field>
                <Field label="Relation">
                  <input style={inp} type="text" name="emergencyContactRelation" placeholder="Relation" value={formData.emergencyContactRelation} onChange={handleChange} />
                </Field>
                <Field label="Phone 1">
                  <input style={inp} type="text" name="emergencyContactPhone1" placeholder="Phone 1" value={formData.emergencyContactPhone1} onChange={handleChange} />
                </Field>
                <Field label="Phone 2">
                  <input style={inp} type="text" name="emergencyContactPhone2" placeholder="Phone 2" value={formData.emergencyContactPhone2} onChange={handleChange} />
                </Field>
              </div>
            </div>

            {/* Documents */}
            <div style={{ marginBottom: 16 }}>
              <SectionHead icon="ti ti-file-upload" label="Documents & Photo" color={COLORS.gray} />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px 14px' }}>

                {/* Patient Photo */}
                <Field label="Patient Photo" error={errors.profileImage}>
                  {formData.base64Image ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <img
                        src={`data:image/png;base64,${formData.base64Image}`}
                        style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover', border: '1px solid #dde3eb' }}
                        alt="Preview"
                      />
                      <button
                        onClick={handleRemoveImage}
                        style={{ height: 28, padding: '0 10px', borderRadius: 6, border: '1px solid #fecaca', background: '#fff5f5', color: COLORS.red, cursor: 'pointer', fontSize: 11 }}
                      >
                        <i className="ti ti-trash" /> Remove
                      </button>
                      <input type="hidden" name="base64Image" value={formData.base64Image} />
                    </div>
                  ) : (
                    <input style={{ ...inp, padding: '3px 9px', height: 32 }} type="file" name="profileImage" onChange={handleFileChange} />
                  )}
                </Field>

                <Field label="CPR Scan">
                  <input style={{ ...inp, padding: '3px 9px', height: 32 }} type="file" name="cprScan" onChange={handleFileChange} />
                </Field>

                <Field label="Passport Copy">
                  <input style={{ ...inp, padding: '3px 9px', height: 32 }} type="file" name="passportCopy" onChange={handleFileChange} />
                </Field>
              </div>
            </div>

            {/* Submit */}
            <div style={{ borderTop: '1px solid #e8ecf0', paddingTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
              <button type="submit" className="bgBtn" style={{ padding: '7px 28px', fontSize: 13 }}>
                <i className="ti ti-user-check" style={{ marginRight: 6 }} />
                Register Patient
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  )
};
export default AddPatientForm;
